import React, { useState, useEffect, useCallback, useRef } from "react";
import { db } from "../firebase";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
  getDoc,
} from "firebase/firestore";
import { ArrowDown2, Eye, EyeSlash, Flag, Play, Trash, VideoPlay } from "iconsax-reactjs";
import {
  reelDoc,
  setReelStatus,
  deleteReel,
  ReelPropertyUnavailableError,
} from "../utils/reelsShared";
import ReelVideoModal from "./reels/ReelVideoModal";
import ReelConfirmModal from "./reels/ReelConfirmModal";
import "./ReportsPanel.css";

const STATUS_TABS = [
  { id: "open", label: "Open" },
  { id: "resolved", label: "Resolved" },
  { id: "dismissed", label: "Dismissed" },
  { id: "all", label: "All" },
];

const REASON_LABELS = {
  "Spam or scam": "Spam or scam",
  "Harassment or abusive language": "Harassment or abuse",
  "Fake listing or fraud": "Fake listing or fraud",
  "Property already rented / sold": "Rented or sold",
  "Incorrect price, size, or location": "Incorrect info",
  "Spam or duplicate listing": "Spam or duplicate",
  "Inappropriate or offensive photos": "Inappropriate content",
  "Inappropriate content": "Inappropriate content",
  Other: "Other",
};

const STATUS_CHIPS = {
  open: { label: "Open", className: "chip chip-dot chip-warning" },
  resolved: { label: "Resolved", className: "chip chip-dot chip-success" },
  dismissed: { label: "Dismissed", className: "chip chip-dot" },
};

const EMPTY_COPY = {
  open: {
    title: "No open reports",
    body: "When app users report a chat, a listing or a Reel, it lands here for review. Resolve it once you have acted, or dismiss it if nothing needs doing.",
  },
  resolved: {
    title: "No resolved reports",
    body: "Reports you mark resolved move here. You can reopen any of them.",
  },
  dismissed: {
    title: "No dismissed reports",
    body: "Reports you dismiss move here. You can reopen any of them.",
  },
  all: {
    title: "No reports yet",
    body: "When app users report a chat, a listing or a Reel, it appears here for review.",
  },
};

const COLUMN_COUNT = 7;

function formatTime(timestamp) {
  if (!timestamp) return "";
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp.seconds * 1000);
  return date.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function getInitials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

export default function ReportsPanel() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("open");
  const [userCache, setUserCache] = useState({});
  const [busyId, setBusyId] = useState(null);
  // Rows whose detail (reporter's note, the Reel, the reference) is open.
  const [expanded, setExpanded] = useState({});

  // Reported reels, resolved once per reelId ever seen. `false` marks one that
  // is already gone: a report can outlive the content it is about.
  const [reelCache, setReelCache] = useState({});
  const reelListeners = useRef(new Map());
  const [previewReel, setPreviewReel] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [reelBusyId, setReelBusyId] = useState(null);

  useEffect(() => {
    const q = query(collection(db, "reports"), orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(q, (snap) => {
      const list = [];
      snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
      setReports(list);
      setLoading(false);
    }, (err) => {
      console.error("Reports fetch failed:", err);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Resolve reporter/reported display info lazily, one lookup per uid ever seen.
  const resolveUser = useCallback((uid) => {
    if (!uid || userCache[uid] !== undefined) return;
    setUserCache((prev) => ({ ...prev, [uid]: null })); // mark in-flight
    getDoc(doc(db, "users", uid))
      .then((snap) => {
        setUserCache((prev) => ({ ...prev, [uid]: snap.exists() ? snap.data() : false }));
      })
      .catch(() => {
        setUserCache((prev) => ({ ...prev, [uid]: false }));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userCache]);

  useEffect(() => {
    reports.forEach((r) => {
      resolveUser(r.reporterId);
      resolveUser(r.reportedUserId);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reports]);

  // Pull in the reel behind each reel report so it can be reviewed and acted on
  // without leaving the queue. A live listener per reel keeps the strip honest
  // after a hide or a delete performed right here.
  //
  // The listeners are held in a ref and torn down only on unmount, NOT in this
  // effect's cleanup: `reports` is itself a live snapshot, so it re-runs
  // whenever any report changes (marking one resolved is enough). Cleaning up
  // per run would kill every reel listener while the already-subscribed guard
  // stopped them from being re-created, freezing each strip on its last value.
  useEffect(() => {
    reports.forEach((r) => {
      const reelId = r.reelId;
      if (r.type !== "reel" || !reelId || reelListeners.current.has(reelId)) return;
      reelListeners.current.set(
        reelId,
        onSnapshot(
          reelDoc(reelId),
          (snap) => {
            setReelCache((prev) => ({
              ...prev,
              [reelId]: snap.exists() ? { id: snap.id, ...snap.data() } : false,
            }));
          },
          (err) => {
            console.error("Failed to load reported reel:", err);
            // Distinct from `false` (deleted): a permissions/network failure
            // must not tell the moderator the Reel is already gone.
            setReelCache((prev) => ({ ...prev, [reelId]: "error" }));
          }
        )
      );
    });
  }, [reports]);

  useEffect(() => {
    const listeners = reelListeners.current;
    return () => {
      listeners.forEach((unsubscribe) => unsubscribe());
      listeners.clear();
    };
  }, []);

  const filteredReports = activeTab === "all"
    ? reports
    : reports.filter((r) => (r.status || "open") === activeTab);

  const statusCounts = reports.reduce((acc, r) => {
    const status = r.status || "open";
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});
  const countFor = (tabId) => (tabId === "all" ? reports.length : statusCounts[tabId] || 0);

  const setStatus = async (reportId, status) => {
    setBusyId(reportId);
    try {
      await updateDoc(doc(db, "reports", reportId), { status });
    } catch (err) {
      console.error("Failed to update report status:", err);
      alert("Something went wrong. Please try again.");
    } finally {
      setBusyId(null);
    }
  };

  const toggleReelStatus = async (reel) => {
    const nextStatus = reel.status === "active" ? "inactive" : "active";
    setReelBusyId(reel.id);
    try {
      await setReelStatus(reel, nextStatus);
    } catch (err) {
      if (err instanceof ReelPropertyUnavailableError) {
        alert("That listing was deleted, so this Reel can no longer be published.");
      } else {
        console.error("Failed to update reel status:", err);
        alert("Could not update the Reel. Please try again.");
      }
    } finally {
      setReelBusyId(null);
    }
  };

  const confirmDeleteReel = async () => {
    if (!pendingDelete) return;
    setReelBusyId(pendingDelete.id);
    try {
      await deleteReel(pendingDelete);
      setPendingDelete(null);
    } catch (err) {
      console.error("Failed to delete reel:", err);
      alert("Could not delete the Reel. Please try again.");
    } finally {
      setReelBusyId(null);
    }
  };

  const toggleExpanded = (reportId) => {
    setExpanded((prev) => ({ ...prev, [reportId]: !prev[reportId] }));
  };

  // The reported reel, inline: enough to judge it and act, without a detour
  // through the Reels panel.
  const renderReelStrip = (report) => {
    const reel = reelCache[report.reelId];
    if (reel === undefined) {
      return <div className="rp-reel-note">Loading Reel…</div>;
    }
    if (reel === false) {
      return <div className="rp-reel-note">This Reel has already been removed.</div>;
    }
    if (reel === "error") {
      return <div className="rp-reel-note">Couldn't load this Reel. Refresh to try again.</div>;
    }

    const isHidden = reel.status !== "active";
    const busy = reelBusyId === reel.id;

    return (
      <div className="rp-reel">
        <button
          type="button"
          className="rp-reel-thumb"
          onClick={() => setPreviewReel(reel)}
          aria-label="Play the reported Reel"
        >
          {reel.thumbnailUrl ? (
            <img src={reel.thumbnailUrl} alt="" />
          ) : (
            <span className="rp-reel-thumb-empty"><VideoPlay size={20} variant="Linear" /></span>
          )}
          <span className="rp-reel-play"><Play size={14} variant="Bold" /></span>
        </button>

        <div className="rp-reel-info">
          <span className="rp-reel-caption">{reel.caption || "Property Reel"}</span>
          <span className="rp-reel-meta">
            <span className={isHidden ? "chip" : "chip chip-success"}>
              {isHidden ? "Hidden from feed" : "Live in feed"}
            </span>
            <span>{reel.views || 0} views</span>
          </span>
          <div className="rp-reel-actions">
            <button
              type="button"
              className="btn-secondary btn-sm"
              onClick={() => toggleReelStatus(reel)}
              disabled={busy}
            >
              {isHidden ? <Eye size={16} variant="Linear" /> : <EyeSlash size={16} variant="Linear" />}
              {isHidden ? "Publish Reel" : "Hide Reel"}
            </button>
            <button
              type="button"
              className="btn-danger btn-sm"
              onClick={() => setPendingDelete(reel)}
              disabled={busy}
            >
              <Trash size={16} variant="Linear" />
              Delete Reel
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderUser = (uid, fallbackLabel) => {
    const info = userCache[uid];
    const name = info ? (info.name || fallbackLabel) : fallbackLabel;
    return (
      <div className="rp-user">
        <span className="avatar rp-avatar">
          {info && info.avatarUrl ? <img src={info.avatarUrl} alt="" /> : getInitials(name)}
        </span>
        <div className="rp-user-text">
          <span className="rp-user-name" title={name}>{name}</span>
          {info && info.phoneNumber && <span className="rp-user-phone">{info.phoneNumber}</span>}
        </div>
      </div>
    );
  };

  // What the report is about, in a few words for the table.
  const targetLabel = (r) => {
    if (r.type === "reel") {
      const reel = reelCache[r.reelId];
      if (reel && typeof reel === "object") return reel.caption || "Property Reel";
      if (reel === false) return "Removed Reel";
      return "Reel";
    }
    if (r.type === "property") return r.propertyTitle || r.propertyId;
    return r.conversationId ? "Conversation" : "No conversation recorded";
  };

  const referenceLabel = (r) =>
    r.type === "reel"
      ? `Reel: ${r.reelId || "unknown"}`
      : r.type === "property"
        ? `Property: ${r.propertyTitle || r.propertyId}`
        : r.conversationId
          ? `Conversation: ${r.conversationId}`
          : "No conversation recorded";

  const renderSkeletonRows = () =>
    Array.from({ length: 5 }, (_, i) => (
      <tr key={`skeleton-${i}`} className="rp-skeleton-row" aria-hidden="true">
        <td>
          <span className="skeleton rp-skel-chip" />
          <span className="skeleton rp-skel-line" />
        </td>
        <td><span className="skeleton rp-skel-line rp-skel-wide" /></td>
        <td><span className="skeleton rp-skel-line" /></td>
        <td><span className="skeleton rp-skel-line" /></td>
        <td><span className="skeleton rp-skel-line rp-skel-short" /></td>
        <td><span className="skeleton rp-skel-chip" /></td>
        <td />
      </tr>
    ));

  const empty = EMPTY_COPY[activeTab] || EMPTY_COPY.all;

  return (
    <div className="rp-panel">
      <div className="toolbar">
        <div className="segmented rp-status-filter" role="group" aria-label="Filter by status">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              aria-pressed={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
              {!loading && <span className="rp-seg-count">{countFor(tab.id)}</span>}
            </button>
          ))}
        </div>
      </div>

      {!loading && filteredReports.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <span className="empty-state-icon"><Flag size={22} variant="Linear" /></span>
            <h3>{empty.title}</h3>
            <p>{empty.body}</p>
          </div>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="table rp-table">
            <thead>
              <tr>
                <th scope="col">Reported</th>
                <th scope="col">Reason</th>
                <th scope="col">Reported user</th>
                <th scope="col">Reported by</th>
                <th scope="col">Date</th>
                <th scope="col">Status</th>
                <th scope="col"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {loading
                ? renderSkeletonRows()
                : filteredReports.map((r) => {
                  const status = r.status || "open";
                  const chip = STATUS_CHIPS[status] || { label: status, className: "chip" };
                  const isOpen = !!expanded[r.id];
                  const detailId = `rp-detail-${r.id}`;
                  return (
                    <React.Fragment key={r.id}>
                      <tr className={isOpen ? "is-linked rp-row-open" : undefined}>
                        <td data-label="Reported">
                          <div className="rp-target">
                            <span className="chip">
                              {r.type === "reel" ? "Reel" : r.type === "property" ? "Listing" : "Chat"}
                            </span>
                            <span className="rp-target-label" title={targetLabel(r)}>{targetLabel(r)}</span>
                          </div>
                        </td>
                        <td data-label="Reason">
                          <div className="rp-reason">
                            <span className="rp-reason-label">{REASON_LABELS[r.reason] || r.reason}</span>
                            {r.details ? <span className="rp-reason-details">{r.details}</span> : null}
                          </div>
                        </td>
                        <td data-label="Reported user">{renderUser(r.reportedUserId, "Unknown user")}</td>
                        <td data-label="Reported by">{renderUser(r.reporterId, "Unknown user")}</td>
                        <td data-label="Date" className="rp-nowrap">{formatTime(r.createdAt)}</td>
                        <td data-label="Status"><span className={chip.className}>{chip.label}</span></td>
                        <td className="rp-actions-cell">
                          <div className="rp-actions">
                            {status !== "resolved" && (
                              <button
                                type="button"
                                className="btn-tonal btn-sm"
                                disabled={busyId === r.id}
                                onClick={() => setStatus(r.id, "resolved")}
                              >
                                Resolve
                              </button>
                            )}
                            {status !== "dismissed" && (
                              <button
                                type="button"
                                className="btn-secondary btn-sm"
                                disabled={busyId === r.id}
                                onClick={() => setStatus(r.id, "dismissed")}
                              >
                                Dismiss
                              </button>
                            )}
                            {(r.status === "resolved" || r.status === "dismissed") && (
                              <button
                                type="button"
                                className="btn-secondary btn-sm"
                                disabled={busyId === r.id}
                                onClick={() => setStatus(r.id, "open")}
                              >
                                Reopen
                              </button>
                            )}
                            <button
                              type="button"
                              className={`btn-icon rp-expand ${isOpen ? "is-open" : ""}`}
                              onClick={() => toggleExpanded(r.id)}
                              aria-expanded={isOpen}
                              aria-controls={detailId}
                              aria-label={isOpen ? "Hide report details" : "Show report details"}
                              title={isOpen ? "Hide details" : "Details"}
                            >
                              <ArrowDown2 size={16} variant="Linear" />
                            </button>
                          </div>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr id={detailId} className="rp-detail-row">
                          <td colSpan={COLUMN_COUNT}>
                            <div className="rp-detail">
                              <div className="rp-detail-block">
                                <span className="rp-detail-label">Reporter's note</span>
                                {r.details ? (
                                  <p className="rp-detail-text">{r.details}</p>
                                ) : (
                                  <p className="rp-detail-text muted">No note was added.</p>
                                )}
                              </div>
                              {r.type === "reel" && r.reelId ? (
                                <div className="rp-detail-block">
                                  <span className="rp-detail-label">Reported Reel</span>
                                  {renderReelStrip(r)}
                                </div>
                              ) : null}
                              <div className="rp-detail-block">
                                <span className="rp-detail-label">Reference</span>
                                <code className="rp-ref">{referenceLabel(r)}</code>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
            </tbody>
          </table>
        </div>
      )}

      {previewReel && <ReelVideoModal reel={previewReel} onClose={() => setPreviewReel(null)} />}

      <ReelConfirmModal
        open={!!pendingDelete}
        title="Delete this Reel?"
        message="The video, its cover, its comments and all of its stats will be permanently removed. This cannot be undone."
        confirmLabel="Delete Reel"
        busy={!!pendingDelete && reelBusyId === pendingDelete.id}
        onConfirm={confirmDeleteReel}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}
